import React from 'react';
import { Calendar as ReactBigCalendar, momentLocalizer } from 'lib/react-big-calendar';
import withDragAndDrop from 'lib/react-big-calendar/addons/dragAndDrop';
import moment from 'moment-timezone';
import 'moment/locale/en-gb';
import queryString from 'query-string';

// Services & Helpers
import API from 'API';
import Print from 'Print';
import DateHelpers from 'helpers/DateHelpers';
import TextHelpers from 'helpers/TextHelpers';
import ReferenceHelpers from 'helpers/ReferenceHelpers';

// Components
import Map from 'components/appt/Map';
import EditAppt from 'components/appt/EditAppt';
import DatePicker from 'components/common/DatePicker';
import Loader from 'components/common/Loader';
import ErrorBoundary from 'components/ErrorBoundary';
import CustomerCommunicationModal from 'components/customer/CustomerCommunication/CustomerCommunicationModal';
import PreviewEmailModal from 'components/outgoing/PreviewEmailModal';
import EditNotesModal from 'components/common/EditNotesModal';

// CSS
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss'

const calendarLocaliser = momentLocalizer(moment)
const DragAndDropCalendar = withDragAndDrop(ReactBigCalendar);
moment.tz.setDefault('Europe/London');

//-----------------------------------------------------------------

class Calendar extends React.Component {

    constructor(props) {
        super(props);

        this.editCustomerCommModalRef = React.createRef();
        this.previewEmailModalRef = React.createRef();
        this.moveOrResize = this.moveOrResize.bind(this);
        this.editNotesModalRef = React.createRef();

        this.state = {
            isLoading: true,
            time: null,
            technicians: null,
            allTechnicians: null,
            technicianUserID: null,
            duration: 30,
            isPrintingCalendar: {},
            notesLookup: {},
            baseAddressLookup: {},
            hideMapLines: props.loginInfo.user.hideMapLines,
            rememberDate: (localStorage.getItem('calendar-remember-date') == 'true'),
            hideMap: (localStorage.getItem('calendar-hide-map') == 'true'),
            verticalMap: (localStorage.getItem('calendar-vertical-map') == 'true')
        };

        // Load initial view from queryString
        const qs = queryString.parse(this.props.history.location.search);
        if (qs.date) {
            this.state.startDate = new Date(qs.date);
        }
        // If date not specified and we are set to remember date
        else if (this.state.rememberDate) {
            try {
                const calendarDateRememberedOn = localStorage.getItem('calendar-date-remembered-on');
                if (!calendarDateRememberedOn || calendarDateRememberedOn == moment().format('YYYY-MM-DD')) {
                    let date = localStorage.getItem('calendar-date');
                    if (date) {
                        date = new Date(date);
                        this.state.startDate = date;
                    }
                    this.state.view = localStorage.getItem('calendar-view');
                }
            } catch (error) {
            }
        }
        if (!this.state.startDate) {
            this.state.startDate = moment().startOf('day').toDate();
        }
        if (!this.state.view) {
            this.state.view = 'day';
        }
        this.state.date = this.state.startDate;

        if (qs.apptID) {
            this.state.isEditingAppt = true;
            this.state.apptID = parseInt(qs.apptID);
        }
        if (qs.customerID) {
            this.state.isEditingAppt = true;
            this.state.customerID = parseInt(qs.customerID);
        }
        if (qs.propertyID) {
            this.state.isEditingAppt = true;
            this.state.propertyID = parseInt(qs.propertyID);
        }
        if (qs.rebookID) {
            this.state.rebookID = parseInt(qs.rebookID);
        }
        if (qs.prevPage) {
            this.state.prevPage = qs.prevPage;
        }
    }

    componentDidMount() {
        this.load();
        this.props.fns.setTitle('Calendar');
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.viewTechnicianUserID !== prevState.viewTechnicianUserID) {
            this.load();
        }
    }


    async debounceLoad() {
        window.clearTimeout(this.loadTimeout);
        this.loadTimeout = window.setTimeout(() => {
            this.load();
        }, 200);
    }

    async load() {
        const { startDate, view } = this.state;
        let technicianUserID = this.state.viewTechnicianUserID;
        this.setState({
            areApptsLoading: true,
            notesLookup: {},
            baseAddressLookup: {}
        });

        // Load technicians
        let { allTechnicians, viewTechnicianUserID } = this.state;
        if (!allTechnicians) {
            allTechnicians = await API.call('user/list', { isTechnician: true });
            if (!viewTechnicianUserID && allTechnicians.length == 1) {
                viewTechnicianUserID = allTechnicians[0].id;
            }
        }
        const technicians = (technicianUserID ? allTechnicians.filter(t => t.id == technicianUserID) : [ ...allTechnicians ]);
        const techniciansByID = {};
        technicians.forEach(t => {
            techniciansByID[t.id] = t;
        });

        // Load appointments
        const date = moment(startDate).format('YYYY-MM-DD');
        const calendar = await API.call('appt/get-calendar', {
            date,
            technicianUserID,
            view
        });
        if (moment(calendar.date).format('YYYY-MM-DD') != date) {
            return;
        }

        // Group appts by technician
        const apptsByTechnician = {};
        calendar.appts.forEach(appt => {
            if (appt.property && appt.property.address && appt.property.address.latitude && appt.property.address.longitude) {
                if (!apptsByTechnician[appt.technicianUserID]) {
                    apptsByTechnician[appt.technicianUserID] = [];
                }
                apptsByTechnician[appt.technicianUserID].push(appt);
            }
        });

        this.setState({
            isLoading: false,
            areApptsLoading: false,
            appts: calendar.appts,
            viewTechnicianUserID,
            notesLookup: calendar.notesLookup,
            baseAddressLookup: calendar.baseAddressLookup,
            apptsByTechnician,
            allTechnicians,
            technicians,
            rotas: calendar.rotas,
            techniciansByID,
            travelStatsByAppt: {},
            travelStatsByTechnician: {}
        }, () => {
            this.debounceLoadTravelStats();
        });
    }

    debounceLoadTravelStats() {
        clearTimeout(this.loadTravelStatsTimeout);
        this.loadTravelStatsTimeout = setTimeout(() => {
            this.loadTravelStats();
        }, 500);
    }

    async loadTravelStats() {
        const { appt, technicians, techniciansByID, isRescheduling, view, baseAddressLookup } = this.state;
        const startDate = moment(this.state.startDate).format('YYYY-MM-DD');
        const { showWarningOverXMins } = this.props.loginInfo.account;
        const appts = [...this.state.appts];
        let apptsByTechnician = { ...this.state.apptsByTechnician };
        let checkTravelTime = false;
        let warnTravelTime = false;

        // Don't bother getting this for past appts
        let endDateSerial = parseInt(moment(startDate).format('YYMMDD'));
        if (view == 'week') endDateSerial += 7;
        else endDateSerial += 1;
        if (endDateSerial < parseInt(moment().format('YYMMDD'))) {
            return;
        }

        this.setState({ warnTravelTime: false });

        // Init travel stats
        let travelStats = {};
        const travelStatsByAppt = {};
        const travelStatsByTechnician = {};
        technicians.forEach(technician => {
            travelStatsByTechnician[technician.id] = {
                travelTime: 0,
                distance: 0
            }
        });

        if (appts) {
            // If editing a new appt or rescheduling existing, insert it into the list
            if (appt && (!appt.id || isRescheduling) && appt.property && appt.time && appt.technicianUserID) {
                appts.push(appt);
                apptsByTechnician[appt.technicianUserID] = [...apptsByTechnician[appt.technicianUserID] || [], appt];
                DateHelpers.sortApptList(apptsByTechnician[appt.technicianUserID]);
                checkTravelTime = !!showWarningOverXMins;
            }

            // Init travel stats by appt
            appts.forEach(appt => {
                travelStatsByAppt[appt.id] = {};
            });

            // Get a list of postcode pairs
            const postcodePairs = [];
            Object.keys(apptsByTechnician).forEach(technicianUserID => {
                const appts = apptsByTechnician[technicianUserID];
                const technician = techniciansByID[technicianUserID];
                if (!technician) return;
                const notesKey = `${startDate}-${technicianUserID}`;
                const baseAddress = baseAddressLookup[notesKey] || technician.baseAddress;
                let homeAddress = technician.homeAddress;
                if (!homeAddress || !homeAddress.postcode) {
                    homeAddress = baseAddress;
                }
                if (!technician || !appts) return;

                // Base -> First Appt
                if (baseAddress && appts.length > 0 && baseAddress.postcode) {
                    postcodePairs.push(`${baseAddress.postcode}-${appts[0].property.address.postcode}`);
                }

                // Appt -> Next Appt
                appts.forEach((appt, index) => {
                    if (index < appts.length - 1) {
                        const nextAppt = appts[index + 1];
                        if (appt.property && nextAppt.property) {
                            postcodePairs.push(`${appt.property.address.postcode}-${nextAppt.property.address.postcode}`);
                        }
                    }
                });

                // Last Appt -> Home
                if (homeAddress && appts.length > 0 && homeAddress.postcode) {
                    postcodePairs.push(`${appts[appts.length - 1].property.address.postcode}-${homeAddress.postcode}`);
                }
            });

            // Get stats
            if (postcodePairs.length > 0) {
                travelStats = await API.call('appt/get-travel-stats', { postcodePairs });
            }

            // Put travel stats into the right places
            Object.keys(apptsByTechnician).forEach(technicianUserID => {
                const appts = apptsByTechnician[technicianUserID];
                const technician = techniciansByID[technicianUserID];
                if (!technician) return;
                const notesKey = `${startDate}-${technicianUserID}`;
                const baseAddress = baseAddressLookup[notesKey] || technician.baseAddress;
                let homeAddress = technician.homeAddress;
                if (!homeAddress || !homeAddress.postcode) {
                    homeAddress = baseAddress;
                }
                const technicianTravelStat = travelStatsByTechnician[technicianUserID];
                if (!technician || !appts) return null;

                // Base -> First Appt
                if (baseAddress && appts.length > 0) {
                    const key = `${baseAddress.postcode}-${appts[0].property.address.postcode}`;
                    const travelStat = travelStats[key];
                    if (travelStat) {
                        if (!appts[0].id && travelStat.travelTime >= showWarningOverXMins) {
                            warnTravelTime = true;
                        }
                        technicianTravelStat.travelTime += travelStat.travelTime;
                        technicianTravelStat.distance += travelStat.distance;
                        travelStatsByAppt[`technician-${technicianUserID}-start`] = travelStat;
                    }
                }

                // Appt -> Next Appt
                appts.forEach((appt, index) => {
                    if (index < appts.length - 1) {
                        const nextAppt = appts[index + 1];
                        if (appt.property && nextAppt.property) {
                            const key = `${appt.property.address.postcode}-${nextAppt.property.address.postcode}`;
                            const travelStat = travelStats[key];
                            if (checkTravelTime) {
                                if ((!appt.id || !nextAppt.id) && travelStat.travelTime >= showWarningOverXMins) {
                                    warnTravelTime = true;
                                }
                            }
                            travelStatsByAppt[appt.id] = travelStat;
                            technicianTravelStat.travelTime += travelStat.travelTime;
                            technicianTravelStat.distance += travelStat.distance;
                        }
                    }
                });

                // Last Appt -> Home
                if (homeAddress && appts.length > 0) {
                    const lastAppt = appts[appts.length - 1];
                    const key = `${lastAppt.property.address.postcode}-${homeAddress.postcode}`;
                    const travelStat = travelStats[key];
                    if (travelStat) {
                        technicianTravelStat.travelTime += travelStat.travelTime;
                        technicianTravelStat.distance += travelStat.distance;
                        travelStatsByAppt[lastAppt.id] = travelStat;
                    }
                }
            });

            // Show travel time warning
            if (warnTravelTime) {
               this.setState({ warnTravelTime: true });
            }
        }

        this.setState({
            travelStats,
            travelStatsByAppt,
            travelStatsByTechnician
        });
    }

    setDate(date, view) {
        if (!view) view = this.state.view;
        date = moment(date).startOf('day').toDate();

        if (!this.state.startDate || moment(date).format('YYYY-MM-DD') != moment(this.state.startDate).format('YYYY-MM-DD') || this.state.view != view) {
            const startDate = (view == 'day' ? date : moment(date).startOf('isoWeek').toDate());

            this.setState({
                startDate,
                date,
                view,
                appts: []
            }, () => {
                this.debounceLoad();
                this.saveDate();
            });
        }
    }

    ensureDateInView(date) {
        const { startDate, view } = this.state;
        const earliestDate = moment(startDate);
        const latestDate = view === 'day' ? earliestDate : earliestDate.clone().add(6, 'days');

        const dateMoment = moment(date);
        if (dateMoment.isBefore(earliestDate) || dateMoment.isAfter(latestDate)) {
            this.setDate(date);
        }
    }

    setTechnician(viewTechnicianUserID) {
        if (viewTechnicianUserID != this.state.viewTechnicianUserID) {
            this.setState({
                viewTechnicianUserID
            }, () => {
                this.load();
            });
        }
    }

    moveDate(delta) {
        const { view } = this.state;
        const numDays = (view == 'week' ? 7 : 1);
        const date = moment(this.state.startDate).add(delta * numDays, 'days').toDate();
        this.setDate(date, view);
    }

    selectSlot(start, technicianUserID) {
        const newState = {};
        if (!this.state.isRescheduling) {
            newState.isEditingAppt = true;
            newState.apptID = null;
            this.debounceLoadTravelStats();
        }
        if (start) {
            newState.date = moment(start).startOf('day').toDate();
            newState.time = moment(start).format('HH:mm');
        } else {
            newState.date = moment(this.state.startDate).startOf('day').toDate();
            newState.time = null;
        }
        if (technicianUserID) {
            newState.technicianUserID = technicianUserID;
        }
        this.setState(newState);
    }

    cancelNewAppt() {
        this.props.history.replace('/calendar');
        this.setState({
            customerID: null,
            propertyID: null,
            customer: null,
            property: null,
            isRescheduling: false
        });
    }

    toggleAppt(apptID) {
        if (this.state.isRescheduling) return;
        if (this.state.isEditingAppt && apptID == this.state.apptID) {
            this.cancelEditingAppt(false);
        } else {
            this.loadAppt(apptID);
        }
    }

    loadAppt(apptID) {
        this.setState({
            isEditingAppt: true,
            apptID
        });
    }

    cancelEditingAppt(reload) {
        // If we were editing a new appt that had a time and property, 
        // the travel stats will now be out of date, so update them
        const { appt, isRescheduling } = this.state;
        if ((!appt.id && appt.property && appt.technician) || isRescheduling) {
            this.debounceLoadTravelStats();
        }

        // Remove any extra information from the URL
        this.props.history.replace('/calendar');

        // Update
        this.setState({
            isEditingAppt: false,
            appt: null,
            customerID: null,
            isRescheduling: false,
            duration: 15,
            warnTravelTime: false
        }, () => {
            if (reload) {
                this.load();
            }
        });
    }

    moveOrResize(info) {
        const time = moment(info.start).format('HH:mm');
        let duration = moment(info.end).diff(info.start, 'minutes');
        if (duration < 15) {
            duration = 15;
        }
        const technicianUserID = parseInt(info.resourceId);
        if (info.event.apptID) {
            const confirm = window.confirm('Are you sure you want to move/resize this appointment?');
            if (confirm) {
                // Update on screen
                const appts = [...this.state.appts];
                const index = appts.findIndex(a => a.id == info.event.apptID);
                const appt = { ...appts[index] };
                appt.technicianUserID = technicianUserID;
                appt.time = time;
                appt.duration = duration;
                appts[index] = appt;
                this.setState({
                    appts
                }, async () => {
                    // Update on server
                    const { sendBookingNotification } = await API.call('appt/move-from-calendar/' + info.event.apptID, {
                        time,
                        duration,
                        technicianUserID
                    });
                    this.load();

                    // Offer to resend booking notification
                    if (sendBookingNotification) {
                        this.confirmResendBookingNotification(info.event.apptID);
                    }
                });
            }
        } else {
            this.setState({
                time,
                technicianUserID,
                duration
            });
        }
    }

    async confirmResendBookingNotification(apptID) {
        const send = await this.previewEmailModalRef.current.open({
            canSend: true,
            email: {
                type: 'ApptBooked',
                apptID
            }
        });
        if (send) {
            await API.call('appt/trigger-notification', {
                apptID,
                type: 'ApptBooked'
            });
        }
    }

    updateAppt(appt) {
        const oldAppt = this.state.appt;
        this.setState({
            appt
        }, () => {
            if (appt && appt.property && (!oldAppt || !oldAppt.property || oldAppt.propertyID != appt.propertyID || appt.time != oldAppt.time || appt.technicianUserID != oldAppt.technicianUserID)) {
                this.debounceLoadTravelStats();
            }
        });
    }

    async printCalendar(technicianUserID) {
        technicianUserID = parseInt(technicianUserID);

        // Update UI
        let isPrintingCalendar = { ...this.state.isPrintingCalendar }; 
        if (isPrintingCalendar[technicianUserID]) {
            return;
        }
        isPrintingCalendar[technicianUserID] = true;
        this.setState({ isPrintingCalendar });

        // Print
        await Print.url('/api/appt/get-calendar-pdf', {
            date: moment(this.state.startDate).format('YYYY-MM-DD'),
            technicianUserID,
            isInline: true
        }); 

        // Update UI
        isPrintingCalendar = { ...this.state.isPrintingCalendar };
        delete isPrintingCalendar[technicianUserID];
        this.setState({ isPrintingCalendar });
    }

    async showEditCalendarNotesModal(technicianUserID, date) {
        date = moment(date).format('YYYY-MM-DD');
        const notesKey = `${date}-${technicianUserID}`;
        const { notesLookup } = this.state;
        let { baseAddressLookup } = this.state;
        const oldBasePostcode = (baseAddressLookup[notesKey] || {})?.postcode;
        const { notes, basePostcode } = await this.editNotesModalRef.current.open({
            notes: notesLookup[notesKey] || '',
            showBasePostcode: true,
            basePostcode: oldBasePostcode
        });
        notesLookup[notesKey] = notes;
        
        const hasBasePostcodeChanged = (basePostcode != oldBasePostcode);
        
        const promise = API.call('appt/update-calendar-notes', {
            date,
            technicianUserID,
            notes,
            basePostcode
        });
        
        if (hasBasePostcodeChanged) {
            const { baseAddress } = await promise;
            baseAddressLookup = { ...this.state.baseAddressLookup };
            baseAddressLookup[notesKey] = baseAddress;
            this.setState({
                baseAddressLookup,
                notesLookup
            }, () => {
                this.load();
                this.debounceLoadTravelStats();
            });
        } else {
            this.setState({ notesLookup });
        }
    }
    
    onSave(appt, rebooks) {
        const { prevPage, isRescheduling } = this.state;
        let addCustomerComm = !!appt.customerID;
        switch (prevPage) {
            case 'rebooks':
                this.props.history.push(`/rebooks`);
                addCustomerComm = false;
                break;
        }
        if (isRescheduling) {
            this.setState({ isRescheduling: false });
            addCustomerComm = false;
        }
        
        if (addCustomerComm) {
            this.editCustomerCommModalRef.current.open({
                customerID: appt.customerID,
                propertyID: appt.propertyID,
                type: 'Note',
                setNoneAsDefaultRebook: true
            });
        }
        this.debounceLoad();
    }

    toggleView() {
        let { view, date } = this.state;
        view = (view == 'day' ? 'week' : 'day');
        if (view == 'week') {
            date = moment(date).startOf('isoWeek').toDate();
        }
        this.setDate(date, view);
    }

    reschedule() {
        const { appt } = this.state;
        this.setState({
            isRescheduling: true,
            date: appt.date,
            time: appt.time,
            technicianUserID: appt.technicianUserID
        });
    }

    setHideMapLines(hideMapLines) {
        this.setState({ hideMapLines });
        API.call('account/set-hide-map-lines/' + hideMapLines);
    }

    setRememberDate(rememberDate) {
        localStorage.setItem('calendar-remember-date', rememberDate);
        if (rememberDate) {
            this.saveDate();
        } else {
            localStorage.removeItem('calendar-date');
            localStorage.removeItem('calendar-view');
            localStorage.removeItem('calendar-date-remembered-on');
        }
        this.setState({ rememberDate });
    }

    setHideMap(hideMap) {
        if (hideMap) {
            localStorage.setItem('calendar-hide-map', 'true');
        } else {
            localStorage.removeItem('calendar-hide-map');
        };
        this.setState({ hideMap });
    }

    setVerticalMap(verticalMap) {
        if (verticalMap) {
            localStorage.setItem('calendar-vertical-map', 'true');
        } else {
            localStorage.removeItem('calendar-vertical-map');
        };
        this.setState({ verticalMap });
    }
    
    saveDate() {
        localStorage.setItem('calendar-date-remembered-on', moment().format('YYYY-MM-DD'));
        localStorage.setItem('calendar-date', moment(this.state.date).format('YYYY-MM-DD'));
        localStorage.setItem('calendar-view', this.state.view);
    }

    //------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading,
            startDate,
            viewTechnicianUserID,
            isEditingAppt,
            calendarNotes,
            allTechnicians,
            view,
            isRescheduling,
            hideMap,
            verticalMap
        } = this.state;

        if (isLoading) {
            return (
                <section>
                    <Loader />
                </section>
            );
        }

        return (<>

            <section className="calendar control-panel">

                <div className="control-panel-component">

                    <button type="button" className="btn btn-primary" onClick={() => this.moveDate(-1)}>
                        <span className="fa-solid fa-chevron-left" />
                    </button>
                    <DatePicker
                        className="form-control"
                        wrapperClassName="date-picker"
                        value={startDate}
                        onChange={date => this.setDate(date, 'day')}
                    />
                    <button type="button" className="btn btn-primary" onClick={() => this.moveDate(+1)}>
                        <span className="fa-solid fa-chevron-right" />
                    </button>

                </div>

                <div className="control-panel-component">

                    <button type="button" className="btn btn-tertiary" onClick={() => this.setDate(new Date(), 'day')}>
                        <span className="fa-solid fa-calendar-day" />{' '} 
                        Today
                    </button>

                </div>

                <div className="control-panel-component">

                    <button type="button" className="btn btn-tertiary" onClick={() => this.toggleView()}>
                        {view == 'day' ? 'Week view' : 'Day view'}
                    </button>

                </div>
                
                <select
                    className="control-panel-component form-control technician-selector"
                    value={viewTechnicianUserID || ''}
                    onChange={e => this.setTechnician(e.target.value)}
                >
                    <option value="">(All technicians)</option>
                    {allTechnicians.map(technician =>
                        <option key={technician.id} value={technician.id}>
                            {technician.nickname}
                        </option>
                    )}
                </select>

                <div className="control-panel-component current-date">
                    {moment(startDate).format('dddd, DD MMMM YY')}
                </div>

                <div className="control-panel-component ms-auto">

                    {this.renderOptionsDropdown()}

                    {!isEditingAppt &&
                        <button type="button" className="btn btn-primary" onClick={() => this.selectSlot(null, null)}>
                            <span className="fa-regular fa-calendar-plus" />{' '}
                            Add Appt
                        </button>
                    }
                </div>

                {isRescheduling &&
                    <div className="control-panel-component ms-auto alert alert-info">
                        Rescheduling appointment
                        <button className="btn btn-sm btn-secondary ms-3" onClick={() => this.setState({ isRescheduling: false })}>
                            Cancel
                        </button>
                    </div>
                }

            </section>

            <div className={`calendar-page ${hideMap ? 'hide-map' : ''} ${verticalMap ? 'vertical-map' : ''}`}>

                <section className="left">

                    {this.renderCalendar()}

                    {!hideMap && this.renderMap()}

                </section>

                {isEditingAppt &&
                    <section className="right">

                        {this.renderCurrentAppt()}

                    </section>
                }

            </div>

            <CustomerCommunicationModal ref={this.editCustomerCommModalRef} />
            <PreviewEmailModal ref={this.previewEmailModalRef} />
            <EditNotesModal ref={this.editNotesModalRef} />

        </>);
    }

    renderCalendar() {
        const { loginInfo } = this.props;
        const { showTravelTimeAfterXMins } = loginInfo.account;
        const { allTechnicians, technicians, appt, date, startDate, time, duration, technicianUserID, isEditingAppt, travelStatsByAppt, techniciansByID, view, isRescheduling } = this.state;

        // Map appts to events
        const appts = [...this.state.appts];
        if (isEditingAppt) {
            if (appt) {
                if (!appt.id) {
                    //appts.push({ ...appt, technicianUserID, date, time });
                    appts.push(appt);
                }

                // If rescheduling, show a ghost for the suggested new time
                if (isRescheduling && date && time && technicianUserID) {
                    appts.push({
                        date, time, technicianUserID,
                        duration: appt.duration
                    });
                }
            }
            else if (date && time && duration && technicianUserID) {
                // Show ghost for new appt
                appts.push({
                    date, time, duration, technicianUserID
                });
            }
            DateHelpers.sortApptList(appts);
        }
        
        const events = [];
        const backgroundEvents = [];

        appts.forEach(a => {
            let start = DateHelpers.combineDateAndTime(a.date, a.time);
            let end = moment(start).add(a.duration, 'minutes').toDate();
            let travelTime = 0;
            if (travelStatsByAppt) {
                const travelStat = travelStatsByAppt[a.id];
                if (travelStat) {
                    travelTime = travelStat.travelTime;
                }
            }

            //// If the event spans multiple days, split it into multiple events
            //// (RBC doesn't seem to render it at all otherwise)
            //const startDateSerial = parseInt(moment(a.date).format('YYMMDD'));
            //const endDateSerial = parseInt(moment(a.date).add(a.duration, 'minutes').format('YYMMDD'));
            //if (endDateSerial != startDateSerial) {
            //    const diff = endDateSerial - startDateSerial;
            //    end = new Date(moment(start).format('YYYY-MM-DD') + 'T23:45');

            //    for (let i = 1; i <= diff; i++) {
            //        events.push({
            //            ...a,
            //            id: a.id + '-' + i,
            //            type: 'appt',
            //            nonBookingApptType: a.nonBookingApptType,
            //            recurringApptID: a.recurringApptID,
            //            apptID: a.id,
            //            resourceId: `${a.technicianUserID}`,
            //            className: (a.id ?
            //                (appt && a.id == appt.id ? 'active-appt' : '') :
            //                'new-appt'
            //            ),
            //            start: moment(a.date).add(i, 'days').startOf('day').toDate(),
            //            end: moment(moment(a.date).add(i, 'days').format('YYYY-MM-DD')).toDate(),
            //            travelTime
            //        });
            //    }
            //}

            // React-big-calendar doesn't show the event at all if the start or end fall outside of today
            if (startDate && parseInt(moment(start).format('YYMMDDHHmm')) < parseInt(moment(startDate).format('YYMMDDHHMM'))) {
                start = new Date(moment(startDate).format('YYYY-MM-DD') + 'T08:00');
            }
            if (moment(end).format('YYYY-MM-DD') != moment(start).format('YYYY-MM-DD')) {
                end = new Date(moment(start).format('YYYY-MM-DD') + 'T23:45');
            }

            // Add appt event
            const classNames = [];
            if (a.id) {
                if (a.isBookedOnline) {
                    classNames.push('appt-booked-online');
                } else if (a.customer && a.customer.isNew) {
                    classNames.push('appt-new-customer');
                }
                if (appt && appt.id == a.id) {
                    classNames.push('new-appt');
                }
            } else {
                classNames.push('new-appt');
            }
            events.push({
                ...a,
                type: 'appt',
                nonBookingApptType: a.nonBookingApptType,
                recurringApptID: a.recurringApptID,
                apptID: a.id,
                resourceId: `${a.technicianUserID}`,
                className: classNames.join(' '),
                start,
                end,
                travelTime,
                isBookedOnline: a.isBookedOnline,
                isCustomerNew: (a.customer && a.customer.isNew)
            });

            // Add travel time event
            if (typeof (showTravelTimeAfterXMins) && showTravelTimeAfterXMins !== null && travelTime >= showTravelTimeAfterXMins) { // TODO make this configurable
                // Get time (end of the appt, plus travel time)
                start = moment(end).toDate();
                end = moment(start).add(Math.max(travelTime, 30), 'minutes').toDate();

                // Map to event
                backgroundEvents.push({
                    type: 'travel-time',
                    id: `travel-time-${a.id}`,
                    apptID: a.id,
                    resourceId: `${a.technicianUserID}`,
                    isBackgroundEvent: true,
                    className: 'travel-time-appt',
                    start,
                    end,
                    travelTime
                });
            }
        });

        // Get rota info
        const dates = [startDate];
        if (view == 'week') {
            const dateMoment = moment(startDate);
            for (let i = 0; i < 6; i++) {
                dateMoment.add(1, 'day');
                dates.push(dateMoment.toDate());
            }
        }
        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            const dowNumber = moment(date).isoWeekday();
            const dow = TextHelpers.DaysOfWeek[dowNumber - 1];
            const rotaDay = this.state.rotas[dow];

            if (rotaDay) {
                for (let i = 0; i < technicians.length; i++) {
                    const technician = technicians[i];
                    const rotaDayTechnician = rotaDay.find(rd => rd.userID == technician.id);
                    const endOfDay = moment(date).add(1, 'day').add(-15, 'minutes').toDate();

                    // Not working at all
                    if (!rotaDayTechnician) {
                        backgroundEvents.push({
                            type: 'non-working-time',
                            className: 'non-working-time',
                            resourceId: `${technician.id}`,
                            isBackgroundEvent: true,
                            start: moment(date).toDate(),
                            end: endOfDay
                        });
                        continue;
                    }

                    // Start time
                    const startTimeInt = (rotaDayTechnician.startTime ?
                        parseInt(moment(moment(date).format('YYYY-MM-DD') + ' ' + rotaDayTechnician.startTime).format('HHmm')) :
                        0
                    );
                    if (!rotaDayTechnician.startTime || (rotaDayTechnician.startTime && startTimeInt > 700)) {
                        backgroundEvents.push({
                            apptID: `technician-${technician.id}-start`,
                            type: 'non-working-time',
                            className: 'non-working-time',
                            resourceId: `${technician.id}`,
                            isBackgroundEvent: true,
                            start: date,
                            end: moment(moment(date).format('YYYY-MM-DD') + ' ' + (rotaDayTechnician.startTime || '07:00')).toDate()
                        });
                    }

                    // Finish time
                    if (rotaDayTechnician.finishTime) {
                        backgroundEvents.push({
                            type: 'non-working-time',
                            className: 'non-working-time',
                            resourceId: `${technician.id}`,
                            isBackgroundEvent: true,
                            start: moment(moment(date).format('YYYY-MM-DD') + ' ' + rotaDayTechnician.finishTime).toDate(),
                            end: endOfDay
                        });
                    }
                }
            }
        }

        // Map technicians to resources
        const resources = technicians.map(t => ({
            resourceId: `${t.id}`,
            resourceTitle: t.nickname,
            mobileTel: t.mobileTel
        }));
        
        // TODO fix the scrollToTime not working bug
        //min={new Date(1900, 1, 1, 0, 30, 0)}
        //scrollToTime={new Date(1900, 1, 1, 8, 0, 0)}
        return (

            <DragAndDropCalendar
                localizer={calendarLocaliser}
                events={events}
                backgroundEvents={backgroundEvents}
                min={new Date(1900, 1, 1, 7, 0, 0)}
                max={new Date(1900, 1, 1, 23, 30, 0)}
                views={['day', 'week']}
                view={view}
                date={new Date(startDate)}
                step={15}
                timeslots={1}
                resourceIdAccessor="resourceId"
                resourceTitleAccessor="resourceTitle"
                resources={resources}
                onNavigate={e => { /* NOP */ }}
                onView={e => { /* NOP */ }}
                onEventDrop={this.moveOrResize}
                onEventResize={this.moveOrResize}
                draggableAccessor={(event) => event.status == 'Open'}
                resizeableAccessor={() => false}
                onSelecting={() => false}
                selectable
                onSelectEvent={event => this.toggleAppt(event.apptID)}
                onSelectSlot={(slot) => this.selectSlot(slot.start, slot.resourceId)}
                dayLayoutAlgorithm="overlap"
                eventPropGetter={(event, start, end, isSelected) => {
                    const props = {
                        style: {}
                    };
                    if (event.type == 'appt') {
                        props.className = 'appt';
                        if (event.nonBookingApptType) {
                            props.style.backgroundColor = event.nonBookingApptType.backColour;
                            props.style.color = event.nonBookingApptType.textColour;
                        } else {
                            const technician = techniciansByID[`${event.resourceId}`];
                            if (technician) {
                                if (event.isBookedOnline) {
                                    props.style.backgroundColor = (technician.diaryOnlineBackColour || '#FFFFFF');
                                    props.style.color = (technician.diaryOnlineTextColour || '#000000');
                                } else if (event.isCustomerNew) {
                                    props.style.backgroundColor = (technician.diaryNewCustBackColour || '#FFFFFF');
                                    props.style.color = (technician.diaryNewCustTextColour || '#000000');
                                } else {
                                    props.style.backgroundColor = (technician.diaryBackColour || '#FFFFFF');
                                    props.style.color = (technician.diaryTextColour || '#000000');
                                }
                            }
                        }
                    }
                    return props;
                }}
                //formats={{
                //    dateFormat: 'dd',
                //    dayFormat: (date, culture, localizer) => moment(date).format('DDD'),
                //    dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                //        moment(start).format('DD/MM/YYYY') + ' — ' +
                //        moment(end).format('DD/MM/YYYY')
                //}}
                components={{
                    toolbar: () => null,
                    resourceHeader: (info) => {
                        const { notesLookup, baseAddressLookup, view } = this.state;
                        const date = moment(this.state.date).format('YYYY-MM-DD');
                        const technicianUserID = parseInt(info.resource.resourceId);

                        // Get calendar notes / base postcode
                        const notesKey = `${date}-${technicianUserID}`;
                        const notes = notesLookup[notesKey];
                        const baseAddress = baseAddressLookup[notesKey];
                        
                        // Get total travel time for this technician
                        const { travelStatsByTechnician } = this.state;
                        let travelTime = 0;
                        if (travelStatsByTechnician) {
                            const travelStat = travelStatsByTechnician[technicianUserID];
                            if (travelStat) {
                                travelTime = travelStat.travelTime;
                            }
                        }

                        return (<>
                            <div className="rbc-resource-header">
                                <span className="technician-name">
                                    {info.label}

                                    {!this.state.technicianUserID && allTechnicians.length > 1 &&
                                        <button type="button" className="btn btn-tertiary" title="View this technician only" onClick={() => this.setTechnician(info.resource.resourceId)}>
                                            <span className="fa-solid fa-eye" />
                                        </button>
                                    }

                                    <button type="button" className="btn btn-tertiary" title="Print Job Sheet for this technician" onClick={() => this.printCalendar(info.resource.resourceId)}>
                                        {this.state.isPrintingCalendar[parseInt(info.resource.resourceId)] ?
                                            <Loader isInline /> :
                                            <span className="fa-solid fa-print" />
                                        }
                                    </button>

                                    {!!info.resource.mobileTel &&
                                        <button type="button" className="btn btn-tertiary" title={info.resource.mobileTel} onClick={() => {
                                            window.alert(info.resource.resourceTitle + ': ' + info.resource.mobileTel);
                                        }}>
                                            <span className="fa-solid fa-phone" />
                                        </button>
                                    }
                                </span>
                                {travelTime > 0 &&
                                    <span className="technician-travel-time" title={`Total travel time: ${travelTime}`}>
                                        <span className="fa-solid fa-van-shuttle" />{' '}
                                        {travelTime}
                                    </span>
                                }
                            </div>
                            {view == 'day' &&
                                <div className="rbc-resource-header-notes">
                                    {notes || '–'}
                                    {!!baseAddress && 
                                        <span className="rbc-resource-header-base-postcode">
                                            {' '} / {baseAddress.postcode}
                                        </span>
                                    }
    
                                    <button type="button" className="btn btn-tertiary" title="Edit notes / base address" onClick={() => this.showEditCalendarNotesModal(technicianUserID, this.state.startDate)}>
                                        <span className="fa-solid fa-pencil-alt" />
                                    </button>

                                </div>
                            }
                        </>)
                    },
                    week: {
                        header: (info) => {
                            const { notesLookup, baseAddressLookup } = this.state;
                            const date = moment(info.date).format('YYYY-MM-DD');
                            const technicianUserID = parseInt(info.resource.resourceId);

                            // Get calendar notes / base postcode
                            const notesKey = `${date}-${technicianUserID}`;
                            const notes = notesLookup[notesKey];
                            const baseAddress = baseAddressLookup[notesKey];

                            return <>
                                {info.label}
                                
                                <div className="rbc-resource-header-notes">
                                    {notes || '–'}
                                    {!!baseAddress &&
                                        <span className="rbc-resource-header-base-postcode">
                                            {' '} / {baseAddress.postcode}
                                        </span>
                                    }

                                    <button type="button" className="btn btn-tertiary" title="Edit notes / base address" onClick={() => this.showEditCalendarNotesModal(technicianUserID, info.date)}>
                                        <span className="fa-solid fa-pencil-alt" />
                                    </button>

                                </div>
                            </>;
                        }
                    },
                    event: (info) => {
                        const { apptID, nonBookingApptType, customer, type, notes, recurringApptID, isBookedOnline } = info.event;
                        if (!apptID) {
                            return null;
                        }

                        // Get travel time
                        const { travelStatsByAppt } = this.state;
                        let travelTime = 0;
                        if (travelStatsByAppt) {
                            const travelStat = travelStatsByAppt[apptID];
                            if (travelStat) {
                                travelTime = travelStat.travelTime;
                            }
                        }

                        // Determine whether to render travel time icon
                        let travelTimeContent = null;
                        if (travelTime > 0) {
                            const travelTimeColour = ReferenceHelpers.getTravelTimeColour(travelTime, loginInfo.account);
                            travelTimeContent = (
                                <div className="rbc-travel-time" style={{ color: travelTimeColour }} title={`Travel time: ${travelTime} minutes`}>
                                    <span className="fa-solid fa-van-shuttle" />{' '}
                                    {travelTime}
                                </div>
                            );
                        }

                        // Render type-specific
                        if (type == 'appt') {
                            return (<>
                                {nonBookingApptType &&
                                    <div className="rbc-non-booking-appt">
                                        {!!notes &&
                                            <span className="icon fa-solid fa-comment" title={notes} />
                                        }
                                        {!!recurringApptID &&
                                            <span className="icon fa-solid fa-arrows-rotate" title="Recurring appointment" />
                                        }
                                        {nonBookingApptType.name}
                                    </div>
                                }
                                {customer &&
                                    <div className="rbc-customer-property">
                                        {this.renderApptInfo(info.event)}

                                        {isBookedOnline && <>
                                            <span className="icon fa-solid fa-wifi" title="Booked Online" />

                                            {!!notes &&
                                                <span className="icon fa-solid fa-comment" title={notes} />
                                            }
                                        </>}
                                    </div>
                                }
                                {travelTimeContent}
                            </>);
                        }
                        else if (type == 'travel-time') {
                            return (<>Travel time ({info.event.travelTime}min)</>);
                        }
                        else if (type == 'non-working-time') {
                            return travelTimeContent;
                        }
                    }
                }}
            />

        );
    }

    renderApptInfo(evt) {
        const { loginInfo } = this.props;
        const { customer, property } = evt;
        const infoParts = [];
        (loginInfo.account.calendarApptInfos || []).forEach(dsai => {
            switch (dsai) {
                case 'CustomerFullName':
                    infoParts.push(TextHelpers.formatName(customer.title, customer.firstName, customer.lastName));
                    break;
                case 'CustomerFullNameOrCompany':
                    infoParts.push(TextHelpers.formatName(customer.title, customer.firstName, customer.lastName) || customer.companyName);
                    break;
                case 'CustomerLastName':
                    infoParts.push(customer.lastName);
                    break;
                case 'CustomerLastNameOrCompany':
                    infoParts.push(customer.lastName || customer.companyName);
                    break;
                case 'CustomerCompany':
                    infoParts.push(customer.companyName);
                    break;
                case 'CustomerIsNew':
                    if (customer.isNew) {
                        infoParts.push(<span className="fa fa-baby" title="New Customer" />);
                    }
                    break;
                case 'PropertyAddress':
                    if (property) {
                        infoParts.push(property.name);
                    }
                    break;
                case 'PropertyPostcode':
                    if (property) {
                        infoParts.push(property.address.postcode);
                    }
                    break;
                case 'ApptSummary':
                    infoParts.push(TextHelpers.getApptSummary(evt));
                    break;
                case 'ApptTime':
                    infoParts.push(evt.time);
                    break;
                case 'TechnicianNickname':
                    if (evt.technician) {
                        infoParts.push(evt.technician.nickname);
                    }
                    break;
            }
        });

        // Render comma-separated
        const filteredParts = infoParts.filter(ip => !!ip);
        if (filteredParts.length === 0) return null;
        return filteredParts.reduce((acc, part, idx) => {
            if (idx === 0) return [part];
            return acc.concat([', ', part]);
        }, []);
        //return infoParts.filter(ip => !!ip).join(', ');
    }

    renderMap() {
        const { loginInfo } = this.props;
        const { appts, appt, technicians, travelStats, startDate, view, isRescheduling, hideMapLines, areApptsLoading, baseAddressLookup } = this.state;
        return (
            <ErrorBoundary>
                <Map
                    date={startDate}
                    areApptsLoading={areApptsLoading}
                    loginInfo={loginInfo}
                    travelStats={travelStats}
                    isRescheduling={isRescheduling}
                    googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyBU1Tuo1Dm76DqowfMoOiKpvoT8nthjMcA&libraries=&v=weekly"
                    loadingElement={<div style={{ height: '100%' }} />}
                    containerElement={<React.Fragment />}
                    mapElement={<div className="map" />}
                    appts={appts}
                    activeAppt={appt}
                    hideLines={hideMapLines}
                    baseAddressLookup={baseAddressLookup}
                    onClick={apptID => {
                        if (!isRescheduling) {
                            this.loadAppt(apptID);
                        }
                    }}
                    technicians={technicians}
                    view={view}
                />
            </ErrorBoundary>
        );
    }

    renderCurrentAppt() {
        const { loginInfo } = this.props;
        const { apptID, date, time, customerID, propertyID, rebookID, technicianUserID, duration, warnTravelTime } = this.state;

        return (<>

            {loginInfo.accounts.length > 1 &&
                <div className="current-account-name">
                    {loginInfo.account.accountName}
                </div>
            }

            <div className="current-appt">

                <EditAppt
                    id={apptID}
                    date={date}
                    time={time}
                    rebookID={rebookID}
                    propertyID={propertyID}
                    customerID={customerID}
                    technicianUserID={technicianUserID}
                    duration={duration}
                    onApptUpdate={appt => this.updateAppt(appt)}
                    onSetDate={date => this.ensureDateInView(date)}
                    onClose={(reload) => this.cancelEditingAppt(reload)}
                    onSave={(appt, rebooks) => this.onSave(appt, rebooks)}
                    onReschedule={() => this.reschedule()}
                    hasControlPanel
                    warnTravelTime={warnTravelTime}
                    {...this.props}
                />

            </div>

        </>);
    }

    renderOptionsDropdown() {
        const {
            hideMapLines,
            rememberDate,
            hideMap,
            verticalMap
        } = this.state;

        return (
            <div className="btn-group dropdown me-2">
                <button type="button" className="btn btn-tertiary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <span className="fa fa-gear" />{' '}
                </button>
                <ul className="dropdown-menu">
                    <li>
                        <a className="dropdown-item" href="#" onClick={e => {
                            e.preventDefault();
                            this.setHideMap(!hideMap);
                        }}>
                            <div className="form-check">
                                <input
                                    id="hideMap"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={!hideMap || ''}
                                    onChange={() => null}
                                />
                                <label className="form-check-label" htmlFor="hideMap">
                                    Show map
                                </label>
                            </div>
                        </a>
                    </li>

                    {!hideMap && <>
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.setHideMapLines(!hideMapLines);
                            }}>
                                <div className="form-check">
                                    <input
                                        id="hideMapLines"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={!hideMapLines || ''}
                                        onChange={() => null}
                                    />
                                    <label className="form-check-label" htmlFor="hideMapLines">
                                        Route lines
                                    </label>
                                </div>
                            </a>
                        </li>
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.setVerticalMap(!verticalMap);
                            }}>
                                <div className="form-check">
                                    <input
                                        id="verticalMap"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={verticalMap || ''}
                                        onChange={() => null}
                                    />
                                    <label className="form-check-label" htmlFor="verticalMap">
                                        Split screen vertically
                                    </label>
                                </div>
                            </a>
                        </li>
                    </>}

                    <li>
                        <a className="dropdown-item" href="#" onClick={e => {
                            e.preventDefault();
                            this.setRememberDate(!rememberDate);
                        }}>
                            <div className="form-check">
                                <input
                                    id="rememberDate"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={rememberDate || ''}
                                    onChange={e => this.setRememberDate(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="rememberDate">
                                    Remember date
                                </label>
                            </div>
                        </a>
                    </li>

                </ul>
            </div>
        );
    }
}

export default Calendar;